import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadialBarChart, RadialBar, PieChart, Pie, Cell
} from 'recharts';
import { 
  Brain, AlertTriangle, TrendingUp, TrendingDown, Activity, Users,
  Thermometer, Calendar, Shield, RefreshCw, CheckCircle2, Clock,
  Zap, Target, ArrowRight, Snowflake, Sun, Leaf, CloudRain
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RISK_TYPE_CONFIG = {
  hospitalization: { 
    icon: Activity, 
    color: 'text-red-500', 
    bg: 'bg-red-500/10',
    gradient: 'from-red-500/20 to-orange-500/10'
  },
  staff_burnout: { 
    icon: Users, 
    color: 'text-orange-500', 
    bg: 'bg-orange-500/10',
    gradient: 'from-orange-500/20 to-yellow-500/10'
  },
  compliance_drift: { 
    icon: Shield, 
    color: 'text-purple-500', 
    bg: 'bg-purple-500/10',
    gradient: 'from-purple-500/20 to-indigo-500/10'
  },
  seasonal_pattern: { 
    icon: Calendar, 
    color: 'text-blue-500', 
    bg: 'bg-blue-500/10',
    gradient: 'from-blue-500/20 to-cyan-500/10'
  }
};

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500', text: 'text-red-500', ring: 'ring-red-500/30' },
  high: { color: 'bg-orange-500', text: 'text-orange-500', ring: 'ring-orange-500/30' },
  medium: { color: 'bg-yellow-500', text: 'text-yellow-500', ring: 'ring-yellow-500/30' },
  low: { color: 'bg-blue-500', text: 'text-blue-500', ring: 'ring-blue-500/30' }
};

const SEASON_ICONS = {
  winter: Snowflake,
  spring: Leaf,
  summer: Sun,
  fall: CloudRain,
  monthly: Calendar
};

const RiskScoreGauge = ({ score, label }) => {
  const getColor = (s) => {
    if (s >= 75) return '#EF4444';
    if (s >= 50) return '#F59E0B';
    if (s >= 25) return '#22D3EE';
    return '#10B981';
  };

  const data = [{ value: score, fill: getColor(score) }];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <ResponsiveContainer width="100%" height={300}>
          <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <RadialBar background={{ fill: 'hsl(var(--muted))' }} dataKey="value" cornerRadius={10} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color: getColor(score) }}>{score}</span>
        </div>
      </div>
      <span className="text-sm text-muted-foreground mt-2">{label}</span>
    </div>
  );
};

const RiskAlertCard = ({ alert, onAcknowledge }) => {
  const typeConfig = RISK_TYPE_CONFIG[alert.risk_type] || RISK_TYPE_CONFIG.compliance_drift;
  const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
  const TypeIcon = typeConfig.icon;

  return (
    <Card className={`overflow-hidden transition-all duration-200 hover:shadow-lg ${alert.is_acknowledged ? 'opacity-60' : ''}`}>
      <div className={`h-1 ${severityConfig.color}`} />
      <CardContent className="pt-5">
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-xl ${typeConfig.bg} flex items-center justify-center shrink-0`}>
            <TypeIcon className={`h-6 w-6 ${typeConfig.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge className={`${severityConfig.color} text-white`}>{alert.severity}</Badge>
              <Badge variant="outline" className="capitalize">{alert.risk_type.replace('_', ' ')}</Badge>
              <Badge variant="secondary" className="gap-1">
                <Clock className="h-3 w-3" />
                {alert.predicted_timeframe}
              </Badge>
            </div>
            
            <h3 className="font-semibold text-lg mb-1">{alert.title}</h3>
            <p className="text-sm text-muted-foreground mb-3">{alert.description}</p>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold" style={{ color: SEVERITY_CONFIG[alert.severity]?.text?.replace('text-', '#').replace('-500', '') || '#F59E0B' }}>
                  {alert.risk_score}
                </div>
                <span className="text-xs text-muted-foreground">Risk<br/>Score</span>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <span className="text-xs text-muted-foreground">Affected Area</span>
                <p className="font-medium text-sm">{alert.affected_area}</p>
              </div>
            </div>

            {alert.contributing_factors?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium mb-1.5">Contributing Factors:</p>
                <div className="flex flex-wrap gap-1">
                  {alert.contributing_factors.map((factor, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{factor}</Badge>
                  ))}
                </div>
              </div>
            )}

            {alert.recommended_interventions?.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-3 mb-3">
                <p className="text-xs font-medium mb-2">Recommended Interventions:</p>
                <ul className="space-y-1">
                  {alert.recommended_interventions.map((intervention, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      {intervention}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t">
              <span className="text-xs text-muted-foreground">
                {new Date(alert.created_at).toLocaleString()}
              </span>
              {!alert.is_acknowledged ? (
                <Button size="sm" onClick={() => onAcknowledge(alert.id)} data-testid={`acknowledge-${alert.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Acknowledge
                </Button>
              ) : (
                <Badge variant="secondary">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Acknowledged by {alert.acknowledged_by}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SeasonalPatternCard = ({ pattern }) => {
  const SeasonIcon = SEASON_ICONS[pattern.season_or_period?.toLowerCase()] || Calendar;
  const isIncrease = pattern.percentage_change > 0;

  return (
    <Card className="overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
      <CardContent className="pt-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/10 flex items-center justify-center shrink-0">
            <SeasonIcon className="h-6 w-6 text-cyan-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="capitalize">{pattern.season_or_period}</Badge>
              <Badge className={isIncrease ? 'bg-red-500' : 'bg-green-500'}>
                {isIncrease ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                {Math.abs(pattern.percentage_change)}%
              </Badge>
            </div>
            <h4 className="font-semibold mb-1">{pattern.metric_name}</h4>
            <p className="text-sm text-muted-foreground mb-3">{pattern.pattern_description}</p>
            
            {pattern.preventive_recommendations?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium">Preventive Actions:</p>
                {pattern.preventive_recommendations.map((rec, i) => (
                  <p key={i} className="text-sm flex items-start gap-2">
                    <Target className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {rec}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PredictiveAnalytics() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/predictive-analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      toast.error('Failed to load predictive analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  const generateAnalytics = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/predictive-analytics/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Predictive analytics generated', {
          description: `${data.risk_alerts?.length || 0} risk alerts identified`
        });
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error('Failed to generate', { description: error.detail });
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setGenerating(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/predictive-analytics/alerts/${alertId}/acknowledge`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Alert acknowledged');
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const riskDistributionData = dashboardData?.risk_distribution ? [
    { name: 'Critical', value: dashboardData.risk_distribution.critical, fill: '#EF4444' },
    { name: 'High', value: dashboardData.risk_distribution.high, fill: '#F97316' },
    { name: 'Medium', value: dashboardData.risk_distribution.medium, fill: '#EAB308' },
    { name: 'Low', value: dashboardData.risk_distribution.low, fill: '#3B82F6' }
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="predictive-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="predictive-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Brain className="h-8 w-8 text-primary" />
            Predictive Risk Analytics
          </h1>
          <p className="text-muted-foreground mt-1">AI-powered early warning system for quality risks</p>
        </div>
        <Button onClick={generateAnalytics} disabled={generating} className="gap-2" data-testid="generate-analytics-btn">
          {generating ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Analyzing Data...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4" />
              Generate New Analysis
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-cyan-500 to-teal-500" />
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Alerts</p>
                <p className="text-4xl font-bold">{dashboardData?.total_unacknowledged || 0}</p>
              </div>
              <AlertTriangle className={`h-10 w-10 ${dashboardData?.total_unacknowledged > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-red-500 to-orange-500" />
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical Risks</p>
                <p className="text-4xl font-bold text-red-500">{dashboardData?.risk_distribution?.critical || 0}</p>
              </div>
              <Activity className="h-10 w-10 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" />
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Risk Score</p>
                <p className="text-4xl font-bold">{dashboardData?.average_risk_score || 0}</p>
              </div>
              <Target className="h-10 w-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Patterns Found</p>
                <p className="text-4xl font-bold">{dashboardData?.seasonal_patterns?.length || 0}</p>
              </div>
              <Calendar className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Risk Alerts</TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns">Seasonal Patterns</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Risk Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {riskDistributionData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={riskDistributionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {riskDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Shield className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                      <p className="font-medium">No Active Risks</p>
                      <p className="text-sm">Generate analysis to detect risks</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Alerts */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Top Priority Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {dashboardData?.active_alerts?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.active_alerts.slice(0, 5).map((alert, index) => {
                        const severityConfig = SEVERITY_CONFIG[alert.severity];
                        return (
                          <div key={alert.id} className={`p-3 rounded-lg border ring-1 ${severityConfig?.ring}`}>
                            <div className="flex items-start gap-3">
                              <div className={`h-3 w-3 rounded-full mt-1.5 ${severityConfig?.color}`} />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{alert.title}</p>
                                <p className="text-sm text-muted-foreground truncate">{alert.affected_area}</p>
                              </div>
                              <Badge variant="outline">{alert.risk_score}</Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      <p>No alerts to display</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-gradient-to-br from-red-500/5 to-transparent">
              <p className="text-sm text-muted-foreground">Hospitalization Risks</p>
              <p className="text-2xl font-bold mt-1">
                {dashboardData?.active_alerts?.filter(a => a.risk_type === 'hospitalization').length || 0}
              </p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-orange-500/5 to-transparent">
              <p className="text-sm text-muted-foreground">Staff Burnout Risks</p>
              <p className="text-2xl font-bold mt-1">
                {dashboardData?.active_alerts?.filter(a => a.risk_type === 'staff_burnout').length || 0}
              </p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-purple-500/5 to-transparent">
              <p className="text-sm text-muted-foreground">Compliance Drifts</p>
              <p className="text-2xl font-bold mt-1">
                {dashboardData?.active_alerts?.filter(a => a.risk_type === 'compliance_drift').length || 0}
              </p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-blue-500/5 to-transparent">
              <p className="text-sm text-muted-foreground">Seasonal Alerts</p>
              <p className="text-2xl font-bold mt-1">
                {dashboardData?.active_alerts?.filter(a => a.risk_type === 'seasonal_pattern').length || 0}
              </p>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="mt-6">
          {dashboardData?.active_alerts?.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.active_alerts.map((alert, index) => (
                <div key={alert.id} className="animate-fade-in" style={{ animationDelay: `${index * 50}ms` }}>
                  <RiskAlertCard alert={alert} onAcknowledge={acknowledgeAlert} />
                </div>
              ))}
            </div>
          ) : (
            <Card className="py-16">
              <CardContent className="flex flex-col items-center justify-center text-center">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-500/20 to-green-500/10 flex items-center justify-center mb-6">
                  <Shield className="h-10 w-10 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-xl mb-2">No Active Risk Alerts</h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Generate a new analysis to identify potential risks in your hospice operations.
                </p>
                <Button onClick={generateAnalytics} disabled={generating}>
                  <Zap className="h-4 w-4 mr-2" />
                  Generate Analysis
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="patterns" className="mt-6">
          {dashboardData?.seasonal_patterns?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboardData.seasonal_patterns.map((pattern, index) => (
                <div key={pattern.id || index} className="animate-fade-in" style={{ animationDelay: `${index * 50}ms` }}>
                  <SeasonalPatternCard pattern={pattern} />
                </div>
              ))}
            </div>
          ) : (
            <Card className="py-16">
              <CardContent className="flex flex-col items-center justify-center text-center">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/10 flex items-center justify-center mb-6">
                  <Calendar className="h-10 w-10 text-blue-500" />
                </div>
                <h3 className="font-semibold text-xl mb-2">No Seasonal Patterns Detected</h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Enter more historical data across multiple periods to enable seasonal pattern detection.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
